import { generateClient } from "aws-amplify/api";
import { Option } from "db/definitions";
import { recordSentry } from "utils/recordSentry";
import { db } from "../db";

const client = generateClient({ authMode: "userPool" });

/**
 * Given a formTemplateId, queries the indexeddb for all options docs with a matching formTemplateId, returning an array of option documents
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formTemplateId The id of the formTemplate (grandparent) of the option doc
 * @returns All options from the indexeddb with the formTemplateProperty equaling the provided formTemplateId
 */
export async function bulkRetrieveOptionsByFormTemplateId(formTemplateId: string): Promise<Option[]> {
	const options = await db.option.where("formTemplate").equals(formTemplateId).toArray();
	return options;
}

/**
 * Given a fieldTemplateId, queries the indexeddb for all options docs with a matching fieldTemplateId, returning an array of option documents
 * @author Eric Webb <ewebb@factorearth.com>
 * @param fieldTemplateId The id of the formTemplate (grandparent) of the option doc
 * @returns All options from the indexeddb with the formTemplateProperty equaling the provided fieldTemplateId
 */
export async function bulkRetrieveOptionsByFieldTemplateId(fieldTemplateId: string): Promise<Option[]> {
	const options = await db.option.where("fieldTemplateId").equals(fieldTemplateId).toArray();
	return options;
}

/**
 * Given a list of options, puts them all in the indexedDb
 * @param optionList Array of options we want to put in the indexedDb
 */
export async function bulkSaveOptions(optionList: Option[]): Promise<void> {
	try {
		await db.option.bulkPut(optionList);
	} catch (error) {
		recordSentry({ error, msg: "Error putting options in the offline db:" });
	}
}

/**
 * Given an option, parses it down to what's expected for the resolver
 * @author Eric Webb <ewebb@factorearth.com>
 * @param opt The option we want to parse
 * @returns The parsed option
 */
export function createOptionForBatchResolver(opt: Option): Option {
	const timestamp = new Date().toISOString();
	const parsedOption: Option = {
		id: opt.id,
		createdAt: opt.createdAt || timestamp,
		updatedAt: opt.updatedAt || timestamp,
		group: opt.group,
		readGroups: [...opt.readGroups],
		fieldTemplateId: opt.fieldTemplateId,
		formTemplate: opt.formTemplate,
		deleted: opt.deleted || false,
		docId: opt.docId,
		exportActions: opt.exportActions || [],
		exportData: opt.exportData || [],
		label: opt.label,
		linkedId: opt.linkedId,
		value: opt.value,
	};
	return parsedOption;
}

/**
 * Given 2 options, "merges" them by comparing the updated at times
 * @author Eric Webb <ewebb@factorearth.com>
 * @param donor The donor option
 * @param receiver The receiver option
 * @returns The merged option
 */
export function createMergedOptionForBatchResolver(donor: Option, receiver: Option): Option {
	const donorUpdated = new Date(donor.updatedAt);
	const receiverUpdated = new Date(receiver.updatedAt);
	const timeStamp = new Date();
	const evaluation = donorUpdated > receiverUpdated;
	const mergedOption: Option = {
		id: donor.id || receiver.id,
		createdAt: (evaluation ? donor.createdAt : receiver.createdAt) || timeStamp.toISOString(),
		deleted: (evaluation ? donor.deleted : receiver.deleted) || false,
		docId: (evaluation ? donor.docId : receiver.docId) || "",
		exportActions: (evaluation ? donor.exportActions : receiver.exportActions) || [],
		exportData: (evaluation ? donor.exportData : receiver.exportData) || [],
		fieldTemplateId: donor.fieldTemplateId || receiver.fieldTemplateId,
		formTemplate: donor.formTemplate || receiver.formTemplate,
		group: evaluation ? donor.group : receiver.group,
		readGroups: evaluation ? [...donor.readGroups] : [...receiver.readGroups],
		label: (evaluation ? donor.label : receiver.label) || "",
		linkedId: (evaluation ? donor.linkedId : receiver.linkedId) || "",
		updatedAt: (evaluation ? donor.updatedAt : receiver.updatedAt) || timeStamp.toISOString(),
		value: (evaluation ? donor.value : receiver.value) || "",
	};
	return mergedOption;
}

/**
 * Given the parent formTemplate id, deletes all option by the formTemplateId
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formTemplateId The id of the formTemplate
 */
export async function deleteOptionsByFormTemplate(formTemplateId: string): Promise<void> {
	await db.option.where("formTemplate").equals(formTemplateId).delete();
}

/**
 * Queries the count of all Options, multiplies the count by the average size, and returns that value
 * @author Eric Webb <ewebb@factorearth.com>
 * @returns The filesize, in bytes, of all options
 */
export async function getTotalOptionDiskSize(): Promise<number> {
	const number = await db.option.count();
	const AVERAGE_DOCUMENT_SIZE = 250.2;
	return number * AVERAGE_DOCUMENT_SIZE;
}

/**
 * Bulk deletion of the options
 * @param options Options that need to be deleted
 */
export async function bulkDeleteOptions(options: Option[]) {
	try {
		const optionIds = options.map((option) => option.id);
		await db.option.bulkDelete(optionIds);
	} catch (err) {
		recordSentry({ error: err, msg: "Error batch deleting options in offline db" });
	}
}
